import {
    Grid, Typography, Button, Snackbar, Alert, Box, Dialog, DialogActions,
    DialogContent, DialogTitle, TextField, FormControl, InputLabel, Select
} from "@mui/material";
import { Save as IconSave } from "@mui/icons-material";
import { PaperComponentDraggable } from "./PaperComponentDraggable";
import { useEffect, useState } from "react";
import * as ubigeoService from "../services/ubigeoService";

// Dialogo de mantenimiento de ubigeo. Atiende los cuatro niveles con un solo
// componente porque cambian poco: nivel 1 = pais, 2 = departamento,
// 3 = provincia, 4 = ubigeo. El combo del padre se carga al abrir y se
// preselecciona con el nivel padre de la fila desde la que se abrio.
const UbigeoEdit = ({ open, openClose, nivel, entity, setNewSaved, isReadOnly }) => {
    const [iso, setIso] = useState("");
    const [numCode, setNumCode] = useState("");
    const [codigo, setCodigo] = useState("");
    const [descripcion, setDescripcion] = useState("");
    const [idPais, setIdPais] = useState(0);
    const [idDepartamento, setIdDepartamento] = useState(0);
    const [idProvincia, setIdProvincia] = useState(0);
    const [padres, setPadres] = useState([]);

    const [enabledSave, setEnabledSave] = useState(true);
    const [openMessage, setOpenMessage] = useState(false);
    const [propsMessage, setPropsMessage] = useState({ severity: "success", message: "" });

    const titulos = { 1: "Pais", 2: "Departamento", 3: "Provincia", 4: "Ubigeo" };
    const isEdicion = (entity?.id || 0) > 0;
    const isDialogReadOnly = isReadOnly || !enabledSave;

    const onCloseMessage = () => setOpenMessage(false);

    // Al abrir: limpia, trae los valores y carga el listado del padre.
    useEffect(() => {
        if (!open) return;
        setEnabledSave(true);
        setIso(entity?.iso || "");
        setNumCode(entity?.numCode || "");
        setCodigo(entity?.codigo || "");
        setDescripcion(entity?.descripcion || "");
        setIdPais(entity?.idPais || 0);
        setIdDepartamento(entity?.idDepartamento || 0);
        setIdProvincia(entity?.idProvincia || 0);

        if (nivel === 2) {
            ubigeoService.getsPais()
                .then((rows) => setPadres(rows.map((r) => ({ id: r.id, descripcion: r.descripcion }))))
                .catch(() => setPadres([]));
        } else if (nivel === 3) {
            ubigeoService.getsMantenimientoView({ nivel: 2 })
                .then((rows) => setPadres(rows.map((r) => ({ id: r.id, descripcion: r.departamento }))))
                .catch(() => setPadres([]));
        } else if (nivel === 4) {
            ubigeoService.getsMantenimientoView({ nivel: 3 })
                .then((rows) => setPadres(rows.map((r) => ({ id: r.id, descripcion: r.provincia, idDepartamento: r.idDepartamento }))))
                .catch(() => setPadres([]));
        } else {
            setPadres([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, nivel]);

    const advertir = (message) => {
        setPropsMessage({ severity: "error", message });
        setOpenMessage(true);
    };

    const postEntity = async () => {
        if (!descripcion || descripcion.trim() === "") {
            advertir("La descripcion es obligatoria");
            return;
        }
        if (nivel === 2 && !idPais) { advertir("Seleccione el pais"); return; }
        if (nivel === 3 && !idDepartamento) { advertir("Seleccione el departamento"); return; }
        if (nivel === 4 && !idProvincia) { advertir("Seleccione la provincia"); return; }

        try {
            let id = 0;
            if (nivel === 1) {
                id = await ubigeoService.addUpdatePais({
                    id: entity?.id || 0, iso, numCode, descripcion
                });
            } else if (nivel === 2) {
                id = await ubigeoService.addUpdateDepartamento({
                    id: entity?.id || 0, idPais, descripcion
                });
            } else if (nivel === 3) {
                id = await ubigeoService.addUpdateProvincia({
                    id: entity?.id || 0, idDepartamento, descripcion
                });
            } else {
                // El departamento del ubigeo se toma de la provincia elegida:
                // asi no queda inconsistente si se cambia de provincia.
                const prov = padres.find((p) => p.id === idProvincia);
                id = await ubigeoService.addUpdateUbigeo({
                    id: entity?.id || 0, codigo, idProvincia,
                    idDepartamento: prov ? (prov.idDepartamento || 0) : (entity?.idDepartamento || 0),
                    descripcion
                });
            }

            if (id > 0) {
                setPropsMessage({ severity: "success", message: "Se guardaron los datos con exito" });
                setOpenMessage(true);
                setEnabledSave(false);
                if (setNewSaved) setNewSaved();
            } else {
                advertir("No se pudo guardar");
            }
        } catch (e) {
            advertir("Hubo un error");
        }
    };

    return (
        <Dialog
            open={open}
            onClose={openClose}
            fullWidth={true}
            maxWidth={"sm"}
            PaperComponent={PaperComponentDraggable}
        >
            <DialogTitle id="draggable-dialog-title" style={{ cursor: "move" }}>
                <Typography variant="h5" component="span">
                    <Box sx={{ fontWeight: "bold" }}>
                        {(isEdicion ? "Editar " : "Registrar ") + titulos[nivel]}
                    </Box>
                </Typography>
            </DialogTitle>
            <DialogContent style={{ minHeight: "260px" }}>
                <div>
                    <Typography gutterBottom component="span">
                        <Box textAlign="center" padding={1}>
                            <Button variant="outlined" size="small" onClick={postEntity} disabled={isDialogReadOnly}>
                                Guardar
                                <IconSave />
                            </Button>
                        </Box>
                    </Typography>

                    <Grid container spacing={1}>
                        {nivel === 1 && (
                            <>
                                <Grid item xs={12} sm={4}>
                                    <TextField label="ISO" value={iso} fullWidth size="small"
                                        onChange={(e) => setIso(e.target.value)} disabled={isDialogReadOnly} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField label="Num. Codigo" value={numCode} fullWidth size="small"
                                        onChange={(e) => setNumCode(e.target.value)} disabled={isDialogReadOnly} />
                                </Grid>
                            </>
                        )}
                        {(nivel === 2 || nivel === 3 || nivel === 4) && (
                            <Grid item xs={12} sm={12}>
                                <FormControl fullWidth size="small" disabled={isDialogReadOnly}>
                                    <InputLabel shrink>
                                        {nivel === 2 ? "Pais" : nivel === 3 ? "Departamento" : "Provincia"}
                                    </InputLabel>
                                    <Select native size="small"
                                        value={nivel === 2 ? idPais : nivel === 3 ? idDepartamento : idProvincia}
                                        onChange={(e) => {
                                            const v = parseInt(e.target.value) || 0;
                                            if (nivel === 2) setIdPais(v);
                                            else if (nivel === 3) setIdDepartamento(v);
                                            else setIdProvincia(v);
                                        }}
                                    >
                                        <option value={0}>{"(Seleccione)"}</option>
                                        {padres.map((p) => (
                                            <option key={p.id} value={p.id}>{p.descripcion}</option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}
                        {nivel === 4 && (
                            <Grid item xs={12} sm={4}>
                                <TextField label="Codigo" value={codigo} fullWidth size="small"
                                    onChange={(e) => setCodigo(e.target.value)} disabled={isDialogReadOnly} />
                            </Grid>
                        )}
                        <Grid item xs={12} sm={nivel === 4 ? 8 : 12}>
                            <TextField label="Descripcion" value={descripcion} fullWidth size="small"
                                onChange={(e) => setDescripcion(e.target.value)} disabled={isDialogReadOnly} />
                        </Grid>
                    </Grid>

                    <Typography component="span">
                        <Box textAlign="center" padding={1}>
                            <Button variant="outlined" size="small" onClick={postEntity} disabled={isDialogReadOnly}>
                                Guardar
                                <IconSave />
                            </Button>
                        </Box>
                    </Typography>

                    <Snackbar open={openMessage} autoHideDuration={5000} onClose={onCloseMessage}
                        anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                        <Alert onClose={onCloseMessage} severity={propsMessage.severity}>
                            {propsMessage.message}
                        </Alert>
                    </Snackbar>
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={openClose}>Aceptar</Button>
            </DialogActions>
        </Dialog>
    );
};

export default UbigeoEdit;
